/**
 * lib/employerReviewerRoster.js — Phase 1.0 admin reviewer roster reads.
 *
 * Reads via service-role-NEVER (always caller JWT + Supabase RLS).
 * Writes delegate to the LOCKED-LIVE shim through `employerReviews.js`.
 */
import { supabase } from '@/lib/supabase';

/**
 * Returns every reviewer-assignment row visible to the caller for an
 * account. Admin/recruiter RLS policies expose all rows of the account;
 * a reviewer only sees their own — UI gating prevents reviewers from
 * reaching this surface in the first place.
 */
export async function listReviewerAssignments(accountId) {
  if (!accountId) return [];
  const { data, error } = await supabase
    .from('emp_reviewer_assignments')
    .select('assignment_id, account_id, role_id, reviewer_user_id, assignment_status, created_at, updated_at')
    .eq('account_id', accountId)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return data || [];
}

/**
 * Returns every member with role='reviewer' for the account. Used as
 * the reviewer-picker source in the Assign dialog.
 */
export async function listReviewerMembers(accountId) {
  if (!accountId) return [];
  const { data, error } = await supabase
    .from('emp_account_members')
    .select('user_id, role, joined_at')
    .eq('account_id', accountId)
    .eq('role', 'reviewer')
    .order('joined_at', { ascending: false });
  if (error) throw error;
  return data || [];
}

/**
 * Returns the OPEN candidate-review rows (status in pending|in_review)
 * for a single role within an account. Used by the Role Detail
 * "Reviewer coverage" panel.
 *
 * Read-only. Admin/recruiter RLS policy (migration 099,
 * emp_candidate_reviews_select_admin_v1) exposes all rows of the
 * account; reviewer self-RLS would scope to own rows. UI gates this
 * surface to admin/recruiter callers.
 */
export async function listOpenReviewsByRole(accountId, roleId) {
  if (!accountId || !roleId) return [];
  const { data, error } = await supabase
    .from('emp_candidate_reviews')
    .select('review_id, reviewer_user_id, status')
    .eq('account_id', accountId)
    .eq('role_id', roleId)
    .in('status', ['pending', 'in_review']);
  if (error) throw error;
  return data || [];
}

/**
 * Returns DECIDED candidate-review rows (status = decided) for a
 * single role within an account, including the recorded decision so
 * the frontend can derive a calm institutional consensus chip on
 * the Role Detail "Reviewer coverage" panel. Read-only; same RLS
 * gating as `listOpenReviewsByRole`.
 */
export async function listDecidedReviewsByRole(accountId, roleId) {
  if (!accountId || !roleId) return [];
  const { data, error } = await supabase
    .from('emp_candidate_reviews')
    .select('review_id, reviewer_user_id, status, decision')
    .eq('account_id', accountId)
    .eq('role_id', roleId)
    .eq('status', 'decided');
  if (error) throw error;
  return data || [];
}

/**
 * Returns OPEN candidate-review rows (status in pending|in_review)
 * across ALL roles in an account. Used by the Reviewer Roster page
 * to render a per-reviewer workload summary (counts grouped client-
 * side by reviewer_user_id + status).
 *
 * Single read. Admin/recruiter RLS policy gates visibility.
 */
export async function listOpenReviewsByAccount(accountId) {
  if (!accountId) return [];
  const { data, error } = await supabase
    .from('emp_candidate_reviews')
    .select('review_id, reviewer_user_id, status')
    .eq('account_id', accountId)
    .in('status', ['pending', 'in_review']);
  if (error) throw error;
  return data || [];
}

/**
 * Advisory pre-check for the Role Matches "Route to reviewers" button.
 * Returns existing emp_candidate_reviews rows for a (role_id,
 * candidate_user_id) tuple, regardless of status.
 *
 * BEST-EFFORT ONLY:
 *   - races concurrent admin actions
 *   - never claims certainty about "new vs already routed"
 *   - failure to read should NOT block the route action — the caller
 *     should swallow the error and fall back to the default label.
 */
export async function listExistingReviewsForRoleCandidate(roleId, candidateUserId) {
  if (!roleId || !candidateUserId) return [];
  const { data, error } = await supabase
    .from('emp_candidate_reviews')
    .select('review_id, reviewer_user_id, status')
    .eq('role_id', roleId)
    .eq('candidate_user_id', candidateUserId);
  if (error) throw error;
  return data || [];
}

/**
 * RC 1.62 M146 — imported-candidate variant of the advisory
 * pre-check. Same best-effort semantics as the profile variant
 * above; queries by `emp_candidate_id` (Channel-1 path).
 */
export async function listExistingReviewsForRoleEmpCandidate(roleId, empCandidateId) {
  if (!roleId || !empCandidateId) return [];
  const { data, error } = await supabase
    .from('emp_candidate_reviews')
    .select('review_id, reviewer_user_id, status')
    .eq('role_id', roleId)
    .eq('emp_candidate_id', empCandidateId);
  if (error) throw error;
  return data || [];
}
